import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MapPin, ClipboardList, AlertTriangle, Target, Newspaper, Clock, FileText, Pencil, X, Save, Building2, Users, Phone, ClipboardCheck, History, ChevronDown, ChevronRight } from "lucide-react";
import RegionMap from "@/components/RegionMap";
import CornerFeed from "@/components/CornerFeed";
import EnseigneLogo from "@/components/EnseigneLogo";
import { PhotoStrip } from "@/components/PhotoUploader";
import { useAuth } from "@/lib/auth";
import { StoreGeoPin } from "@/components/StoreGeoPin";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.round((d - now) / (1000 * 60 * 60 * 24));
}

function ContractCard({ store, canEdit, onEdit }) {
  const days = daysUntil(store.contract_end);
  const soon = days != null && days < 180;
  const overdue = days != null && days < 0;
  const bg = overdue ? "border-rose-300 bg-rose-50" : soon ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white";
  const dot = overdue ? "bg-rose-500" : soon ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className={`border rounded-2xl p-4 ${bg}`} data-testid="store-contract-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-600"/>
          <div className="font-semibold text-slate-900 text-sm">Contrat</div>
          {store.contract_type && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-900 text-white">{store.contract_type}</span>
          )}
        </div>
        {canEdit && (
          <button data-testid="edit-contract" onClick={onEdit} className="w-7 h-7 grid place-items-center rounded-lg hover:bg-white text-slate-500"><Pencil className="w-3.5 h-3.5"/></button>
        )}
      </div>
      {store.contract_end ? (
        <>
          <div className="mt-2 flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${dot}`}/>
            <div className="text-sm text-slate-700">
              Fin le <b>{new Date(store.contract_end).toLocaleDateString("fr-FR")}</b>
            </div>
          </div>
          <div className={`text-xs mt-1 font-semibold ${overdue ? "text-rose-700" : soon ? "text-amber-700" : "text-emerald-700"}`}>
            {overdue ? `Expiré depuis ${Math.abs(days)} j` : soon ? `Renouvellement dans ${days} j` : `Encore ${days} j`}
          </div>
        </>
      ) : (
        <div className="mt-2 text-xs text-slate-400">Aucune date de fin renseignée{canEdit ? " — cliquez sur le crayon pour l'ajouter" : ""}.</div>
      )}
    </div>
  );
}

function ContractModal({ store, onClose, onSave }) {
  const [type, setType] = useState(store.contract_type || "");
  const [start, setStart] = useState(store.contract_start || "");
  const [end, setEnd] = useState(store.contract_end || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/stores/${store.id}/contract`, {
        contract_type: type || null,
        contract_start: start || null,
        contract_end: end || null,
      });
      toast.success("Contrat mis à jour");
      onSave(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-display text-xl font-semibold">Contrat — {store.name}</h2>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Type de contrat</label>
            <div className="mt-2 flex gap-2">
              {["", "C1", "C2", "C3"].map(t => (
                <button key={t} data-testid={`contract-type-${t || "none"}`} onClick={() => setType(t)}
                        className={`flex-1 px-3 py-2 rounded-xl border text-sm font-semibold ${type === t ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  {t || "—"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Date de début</label>
            <input data-testid="contract-start" type="date" value={start ? start.slice(0,10) : ""}
                   onChange={(e) => setStart(e.target.value)}
                   className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm mt-1"/>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Date de fin</label>
            <input data-testid="contract-end" type="date" value={end ? end.slice(0,10) : ""}
                   onChange={(e) => setEnd(e.target.value)}
                   className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm mt-1"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-white">Annuler</button>
          <button data-testid="contract-save" onClick={save} disabled={saving}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Editable Fiche (Phase 20) ────────────────────────────────────────────
function EditableField({ label, value, testid, onSave, canEdit, type = "text", options = null, placeholder = "" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(value ?? ""); }, [value]);
  const commit = async () => {
    if ((val ?? "") === (value ?? "")) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(val === "" ? null : val);
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec sauvegarde");
    } finally { setSaving(false); }
  };
  const cancel = () => { setVal(value ?? ""); setEditing(false); };
  const displayVal = (value === null || value === undefined || value === "") ? <span className="text-slate-300 italic">—</span> : String(value);
  return (
    <div className="group flex items-start gap-2 py-1.5 border-b border-slate-50 last:border-b-0" data-testid={`fiche-row-${testid}`}>
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide w-40 shrink-0 pt-1">{label}</div>
      <div className="flex-1 min-w-0">
        {!editing && (
          <div className="flex items-center gap-2">
            <div className="text-sm text-slate-800 flex-1 break-words">{displayVal}</div>
            {canEdit && (
              <button onClick={() => setEditing(true)} data-testid={`fiche-edit-${testid}`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 grid place-items-center rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700">
                <Pencil className="w-3 h-3"/>
              </button>
            )}
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-1.5">
            {options ? (
              <SearchSelect value={val} onChange={v => setVal(v)} options={[...options.map(o => ({ value: o.value, label: o.label }))]} placeholder={"—"} data-testid={`fiche-input-${testid}`} size="lg" menuWidth={260}/>
            ) : (
              <input value={val ?? ""} onChange={e => setVal(e.target.value)} data-testid={`fiche-input-${testid}`}
                     type={type} placeholder={placeholder} autoFocus
                     onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") cancel(); }}
                     className="flex-1 min-w-0 px-2 py-1 rounded border border-blue-300 focus:outline-none focus:border-blue-500 text-sm"/>
            )}
            <button onClick={commit} disabled={saving} data-testid={`fiche-save-${testid}`}
                    className="w-7 h-7 grid place-items-center rounded bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 shrink-0">
              <Save className="w-3.5 h-3.5"/>
            </button>
            <button onClick={cancel} data-testid={`fiche-cancel-${testid}`}
                    className="w-7 h-7 grid place-items-center rounded bg-slate-200 text-slate-700 hover:bg-slate-300 shrink-0">
              <X className="w-3.5 h-3.5"/>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Audit Log (Phase 20b) ─────────────────────────────────────────────────
const FIELD_LABELS = {
  name: "Nom du compte", enseigne: "Enseigne", marque: "Marque",
  region_name: "Région", sector_zone: "Secteur", dr_name: "DR", dr_id: "DR",
  bm_name: "BM", bm_id: "BM", responsible_id: "BM", responsible_name: "BM",
  address: "Adresse", surface_m2: "Surface (m²)", taux_commission: "Taux commission",
  date_fdc: "Date FDC", duree_contrat_mois: "Durée contrat (mois)",
  prop_inox: "Propriété Inox", prop_vitrine: "Propriété Vitrine",
  prop_bac_wok: "Propriété Bac Wok", prop_habillage: "Propriété Habillage",
  run_rate: "Run Rate",
  adherent_name: "Adhérent", adherent_contact: "Contact adhérent",
  directeur_name: "Directeur", directeur_contact: "Contact directeur",
  resp_qualite_name: "Resp. Qualité", resp_qualite_contact: "Contact Qualité",
  resp_fichier_name: "Resp. Fichier", resp_fichier_contact: "Contact Fichier",
  comptable_name: "Comptable", comptable_contact: "Contact comptable",
  lat: "Latitude", lng: "Longitude", ville: "Ville",
  contract_type: "Type contrat", contract_start: "Début contrat", contract_end: "Fin contrat",
};

function fmtVal(v) {
  if (v === null || v === undefined || v === "") return <span className="text-slate-300 italic">vide</span>;
  const s = String(v);
  if (s.length > 60) return <span title={s}>{s.slice(0, 60)}…</span>;
  return s;
}
function fmtRelDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StoreAuditLog({ storeId }) {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/stores/${storeId}/audit`);
      setLogs(data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const onUpd = (e) => { if (e.detail?.storeId === storeId) load(); };
    window.addEventListener("orbit-store-updated", onUpd);
    return () => window.removeEventListener("orbit-store-updated", onUpd);
  }, [storeId]);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="store-audit-log">
      <button onClick={() => setOpen(v => !v)} data-testid="audit-toggle"
              className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50 transition-colors">
        {open ? <ChevronDown className="w-4 h-4 text-slate-500"/> : <ChevronRight className="w-4 h-4 text-slate-500"/>}
        <History className="w-4 h-4 text-slate-600"/>
        <div className="font-display font-bold text-sm text-slate-800">Historique des modifications</div>
        <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid="audit-count">
          {logs.length}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 max-h-[400px] overflow-y-auto">
          {loading && <div className="px-4 py-6 text-center text-xs text-slate-400">Chargement…</div>}
          {!loading && logs.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-slate-400 italic">Aucune modification enregistrée sur cette fiche.</div>
          )}
          {!loading && logs.map(l => (
            <div key={l.id} className="px-4 py-2.5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50" data-testid={`audit-entry-${l.id}`}>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[11px] font-bold text-slate-700">{l.user_name}</span>
                <span className="text-[11px] text-slate-400">·</span>
                <span className="text-[11px] text-slate-500">{fmtRelDate(l.at)}</span>
              </div>
              <div className="text-xs text-slate-800 mt-0.5">
                <b className="text-slate-900">{FIELD_LABELS[l.field] || l.field}</b>
                <span className="text-slate-400 mx-1">:</span>
                <span className="line-through text-slate-400">{fmtVal(l.old_value)}</span>
                <span className="text-slate-400 mx-1.5">→</span>
                <span className="text-emerald-700 font-semibold">{fmtVal(l.new_value)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function FicheSection({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
        <Icon className="w-4 h-4 text-slate-600"/>
        <div className="font-display font-bold text-sm text-slate-800">{title}</div>
      </div>
      <div className="px-4 py-2">{children}</div>
    </div>
  );
}

function StoreFiche({ store, canEdit, canEditStructural, drOptions, bmOptions, onPatched, isAdmin, reload }) {
  const patch = (field) => async (value) => {
    const { data } = await api.patch(`/stores/${store.id}`, { [field]: value });
    onPatched(data);
    toast.success("Fiche mise à jour");
    // Signal audit log to refresh
    try { window.dispatchEvent(new CustomEvent("orbit-store-updated", { detail: { storeId: store.id } })); } catch {}
  };
  const bmOpts = bmOptions.map(u => ({ value: u.id, label: u.name }));
  const drOpts = drOptions.map(u => ({ value: u.id, label: u.name }));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="store-fiche">
      <FicheSection title="Identité & arborescence" icon={Building2}>
        <EditableField label="Nom du compte" value={store.name} testid="name" canEdit={canEditStructural} onSave={patch("name")}/>
        <EditableField label="Enseigne" value={store.enseigne} testid="enseigne" canEdit={canEdit} onSave={patch("enseigne")}
                       options={[{value:"Leclerc",label:"Leclerc"},{value:"Intermarché",label:"Intermarché"},{value:"Carrefour",label:"Carrefour"},{value:"Carrefour R",label:"Carrefour R"},{value:"Système U",label:"Système U"}]}/>
        <EditableField label="Marque" value={store.marque} testid="marque" canEdit={canEdit} onSave={patch("marque")}
                       options={[{value:"WASABI",label:"WASABI"},{value:"SORAE",label:"SORAE"}]}/>
        <EditableField label="Région" value={store.region_name || store.dr_name} testid="region" canEdit={canEdit} onSave={patch("region_name")}/>
        <EditableField label="Directeur Régional" value={store.dr_name} testid="dr" canEdit={canEditStructural} onSave={patch("dr_id")} options={drOpts}/>
        <EditableField label="Business Manager" value={store.bm_name || store.responsible_name} testid="bm" canEdit={canEditStructural} onSave={patch("bm_id")} options={bmOpts}/>
        <EditableField label="Secteur" value={store.sector_zone} testid="sector-zone" canEdit={canEdit} onSave={patch("sector_zone")} placeholder="Ex. Sud-Est 1 / CCDR"/>
        <EditableField label="Adresse" value={store.address} testid="address" canEdit={canEdit} onSave={patch("address")}/>
      </FicheSection>

      {(isAdmin || canEdit) && <StoreGeoPin store={store} canEdit={isAdmin} onSaved={reload} />}

      <FicheSection title="Contrat & Corner" icon={ClipboardCheck}>
        <EditableField label="Surface (m²)" value={store.surface_m2} testid="surface" canEdit={canEdit} onSave={patch("surface_m2")}/>
        <EditableField label="Taux commission (%)" value={store.taux_commission} testid="commission" canEdit={canEdit} onSave={patch("taux_commission")} type="number"/>
        <EditableField label="Date FDC" value={store.date_fdc ? String(store.date_fdc).slice(0,10) : ""} testid="date-fdc" canEdit={canEdit} onSave={patch("date_fdc")} type="date"/>
        <EditableField label="Durée contrat (mois)" value={store.duree_contrat_mois} testid="duree" canEdit={canEdit} onSave={patch("duree_contrat_mois")} type="number"/>
        <EditableField label="Propriété Inox" value={store.prop_inox} testid="prop-inox" canEdit={canEdit} onSave={patch("prop_inox")}
                       options={[{value:"Wasabi",label:"Wasabi"},{value:"Client",label:"Client"},{value:"CCD",label:"CCD"}]}/>
        <EditableField label="Propriété Vitrine" value={store.prop_vitrine} testid="prop-vitrine" canEdit={canEdit} onSave={patch("prop_vitrine")}
                       options={[{value:"Wasabi",label:"Wasabi"},{value:"Client",label:"Client"},{value:"CCD",label:"CCD"}]}/>
        <EditableField label="Propriété Bac Wok" value={store.prop_bac_wok} testid="prop-bacwok" canEdit={canEdit} onSave={patch("prop_bac_wok")}
                       options={[{value:"Wasabi",label:"Wasabi"},{value:"Client",label:"Client"},{value:"CCD",label:"CCD"}]}/>
        <EditableField label="Propriété Habillage" value={store.prop_habillage} testid="prop-habillage" canEdit={canEdit} onSave={patch("prop_habillage")}
                       options={[{value:"Wasabi",label:"Wasabi"},{value:"Client",label:"Client"},{value:"CCD",label:"CCD"}]}/>
        <EditableField label="Run Rate (€/an)" value={store.run_rate} testid="run-rate" canEdit={canEdit} onSave={patch("run_rate")} type="number"/>
      </FicheSection>

      <FicheSection title="Contacts Adhérent & Directeur" icon={Users}>
        <EditableField label="Adhérent(e)" value={store.adherent_name} testid="adherent" canEdit={canEdit} onSave={patch("adherent_name")}/>
        <EditableField label="N° / Mail adhérent" value={store.adherent_contact} testid="adherent-contact" canEdit={canEdit} onSave={patch("adherent_contact")}/>
        <EditableField label="Directeur(trice)" value={store.directeur_name} testid="directeur" canEdit={canEdit} onSave={patch("directeur_name")}/>
        <EditableField label="N° / Mail directeur" value={store.directeur_contact} testid="directeur-contact" canEdit={canEdit} onSave={patch("directeur_contact")}/>
      </FicheSection>

      <FicheSection title="Contacts Support magasin" icon={Phone}>
        <EditableField label="Resp. Qualité" value={store.resp_qualite_name} testid="resp-q" canEdit={canEdit} onSave={patch("resp_qualite_name")}/>
        <EditableField label="N° / Mail Qualité" value={store.resp_qualite_contact} testid="resp-q-contact" canEdit={canEdit} onSave={patch("resp_qualite_contact")}/>
        <EditableField label="Resp. Fichier" value={store.resp_fichier_name} testid="resp-f" canEdit={canEdit} onSave={patch("resp_fichier_name")}/>
        <EditableField label="N° / Mail Fichier" value={store.resp_fichier_contact} testid="resp-f-contact" canEdit={canEdit} onSave={patch("resp_fichier_contact")}/>
        <EditableField label="Comptable" value={store.comptable_name} testid="compta" canEdit={canEdit} onSave={patch("comptable_name")}/>
        <EditableField label="N° / Mail Comptable" value={store.comptable_contact} testid="compta-contact" canEdit={canEdit} onSave={patch("comptable_contact")}/>
      </FicheSection>
    </div>
  );
}

export default function StoreDetail({ idOverride, embedded }) {
  const { id: routeId } = useParams();
  const id = idOverride || routeId;
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [directory, setDirectory] = useState({ drs: [], bms: [] });
  const [tab, setTab] = useState("fiche");
  const [contractModal, setContractModal] = useState(false);

  const reload = async () => {
    const { data: d } = await api.get(`/stores/${id}`);
    setData(d);
  };
  useEffect(() => { reload(); }, [id]);
  useEffect(() => {
    api.get("/users/for-chat").then(({ data }) => {
      setDirectory({
        drs: (data || []).filter(u => u.role === "director").sort((a,b) => a.name.localeCompare(b.name)),
        bms: (data || []).filter(u => u.role === "manager" && !u.is_support).sort((a,b) => a.name.localeCompare(b.name)),
      });
    }).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-400 text-sm">Chargement…</div>;
  const { store, activities, issues, plans, journal } = data;

  const isAdmin = user?.role === "admin";
  const isSupport = !!user?.is_support;
  const isBmHere = user?.role === "manager" && store.responsible_id === user.id;
  const canEditFiche = isAdmin || isSupport || isBmHere;
  const canEditStructural = isAdmin;  // only admin changes DR/BM/name
  const canEditContract = isAdmin || user?.role === "director";

  const onPatched = (updatedStore) => setData(d => ({ ...d, store: updatedStore }));

  const TABS = [
    { k: "fiche", l: "Fiche magasin", Icon: FileText },
    { k: "activities", l: `Visites (${activities.length})`, Icon: ClipboardList },
    { k: "issues", l: `Problématiques (${issues.length})`, Icon: AlertTriangle },
    { k: "plans", l: `Plans (${plans.length})`, Icon: Target },
    { k: "journal", l: `Journal (${journal.length})`, Icon: Newspaper },
  ];

  return (
    <div className="space-y-6" data-testid="store-detail">
      <button onClick={() => nav("/stores")} data-testid="store-back"
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4"/> Retour aux magasins
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="relative group">
              <EnseigneLogo enseigne={store.enseigne} logoUrl={store.logo_url} size={56} rounded="lg"/>
              {canEditStructural && (
                <>
                  <input type="file" accept="image/*" id="store-logo-upload" className="hidden"
                         onChange={async (e) => {
                           const file = e.target.files?.[0]; if (!file) return;
                           const fd = new FormData(); fd.append("file", file);
                           try {
                             const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
                             const url = `${api.defaults.baseURL}/files/${data.path}`;
                             await api.patch(`/stores/${store.id}`, { logo_url: url });
                             reload();
                             toast.success("Logo mis à jour");
                           } catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
                           e.target.value = "";
                         }}/>
                  <div className="absolute -bottom-1 -right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button title="Uploader un logo" onClick={()=>document.getElementById("store-logo-upload").click()}
                            data-testid="store-logo-upload-btn"
                            className="w-5 h-5 rounded-full bg-blue-600 text-white grid place-items-center hover:bg-blue-700">
                      <Pencil className="w-2.5 h-2.5"/>
                    </button>
                    {store.logo_url && (
                      <button title="Réinitialiser le logo" onClick={()=>{
                        api.patch(`/stores/${store.id}`, { logo_url: null })
                          .then(()=>{ reload(); toast.success("Logo réinitialisé"); })
                          .catch(e=>toast.error(e.response?.data?.detail || "Erreur"));
                      }} data-testid="store-logo-reset"
                         className="w-5 h-5 rounded-full bg-slate-500 text-white grid place-items-center hover:bg-slate-700">
                        <X className="w-2.5 h-2.5"/>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold text-slate-900">{store.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm">
                {store.marque && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{store.marque}</span>}
                {store.enseigne && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{store.enseigne}</span>}
                {store.region_name && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{store.region_name}</span>}
                {store.sector_zone && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">{store.sector_zone}</span>}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {store.dr_name && <>DR <b className="text-slate-700">{store.dr_name}</b></>}
                {store.dr_name && store.bm_name && " · "}
                {store.bm_name && <>BM <b className="text-slate-700">{store.bm_name}</b></>}
              </div>
              {store.address && <div className="text-xs text-slate-400 mt-1">{store.address}</div>}
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Visites", value: activities.length, color: "bg-blue-50 text-blue-700" },
              { label: "Problématiques", value: issues.length, color: "bg-rose-50 text-rose-700" },
              { label: "Plans", value: plans.length, color: "bg-amber-50 text-amber-700" },
              { label: "Journal", value: journal.length, color: "bg-emerald-50 text-emerald-700" },
            ].map((s) => (
              <div key={s.label} className={`rounded-xl p-3 ${s.color}`}>
                <div className="text-xs font-semibold uppercase tracking-wider opacity-80">{s.label}</div>
                <div className="font-display text-2xl font-bold">{s.value}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            {store.lat && store.lng ? (
              <RegionMap stores={[{ ...store, status: "ok" }]} height={200}/>
            ) : (
              <div className="h-48 rounded-xl bg-slate-100 grid place-items-center text-slate-400 text-sm">Coordonnées non renseignées</div>
            )}
          </div>
          <ContractCard store={store} canEdit={canEditContract} onEdit={() => setContractModal(true)}/>
          <CornerFeed storeId={store.id} storeName={store.name} limit={3}/>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl">
        <div className="flex items-center gap-2 border-b border-slate-100 px-2 overflow-x-auto scrollbar-thin">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab === t.k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <t.Icon className="w-4 h-4"/> {t.l}
            </button>
          ))}
        </div>
        <div className="p-6 space-y-3">
          {tab === "fiche" && (
            <>
              {!canEditFiche && <div className="text-xs text-slate-400 italic mb-2">Vue lecture seule. Modifications réservées à admin, support, ou BM en charge.</div>}
              <StoreFiche store={store} isAdmin={isAdmin} reload={reload} canEdit={canEditFiche} canEditStructural={canEditStructural}
                          drOptions={directory.drs} bmOptions={directory.bms} onPatched={onPatched}/>
              <div className="mt-4"><StoreAuditLog storeId={store.id}/></div>
            </>
          )}
          {tab === "activities" && activities.map((a) => (
            <div key={a.id} className="border border-slate-100 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-900">{a.visit_type}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3.5 h-3.5"/>{a.duration_min} min · {new Date(a.date).toLocaleDateString("fr-FR")}</div>
              </div>
              <div className="text-sm text-slate-600 mt-1">{a.description}</div>
              <div className="text-xs text-slate-400 mt-1">par {a.user_name}</div>
              <PhotoStrip paths={a.photos}/>
            </div>
          ))}
          {tab === "issues" && issues.map((i) => (
            <div key={i.id} className="border border-slate-100 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold text-slate-900">{i.title}</div>
                <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${i.status === "resolved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{i.status}</span>
              </div>
              <div className="text-sm text-slate-600 mt-1">{i.description}</div>
              <PhotoStrip paths={i.photos}/>
            </div>
          ))}
          {tab === "plans" && plans.map((p) => (
            <div key={p.id} className="border border-slate-100 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold text-slate-900">{p.objective}</div>
                <div className="text-sm font-bold text-blue-600">{p.progress}%</div>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-500 to-teal-500" style={{ width: `${p.progress}%` }}/>
              </div>
              {p.deadline && <div className="text-xs text-slate-500 mt-2">Échéance {new Date(p.deadline).toLocaleDateString("fr-FR")}</div>}
            </div>
          ))}
          {tab === "journal" && journal.map((j) => (
            <div key={j.id} className="border border-slate-100 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-900">{j.user_name}</div>
                <div className="text-xs text-slate-400">{new Date(j.created_at).toLocaleString("fr-FR")}</div>
              </div>
              <div className="text-sm text-slate-600 mt-1">{j.content}</div>
              <PhotoStrip paths={j.photos}/>
            </div>
          ))}
          {(tab === "activities" && activities.length === 0) && <div className="text-slate-400 text-sm text-center py-8">Aucune visite.</div>}
          {(tab === "issues" && issues.length === 0) && <div className="text-slate-400 text-sm text-center py-8">Aucune problématique.</div>}
          {(tab === "plans" && plans.length === 0) && <div className="text-slate-400 text-sm text-center py-8">Aucun plan.</div>}
          {(tab === "journal" && journal.length === 0) && <div className="text-slate-400 text-sm text-center py-8">Aucun post.</div>}
        </div>
      </div>

      {contractModal && (
        <ContractModal store={store} onClose={() => setContractModal(false)}
                       onSave={() => { setContractModal(false); reload(); }}/>
      )}
    </div>
  );
}
